<template>
  <v-menu offset-y location="bottom right">
    <template #activator="{ props }">
      <v-btn
        class="add-btn allow-blocklist-tab-add-btn"
        color="primary"
        density="default"
        size="large"
        rounded
        :data-testid="`msp-${isEdr ? 'edr' : 'endpoint-security'}-add-btn`"
        v-bind="props"
      >
        <v-icon class="mr-2" icon="$add" />
        {{ $t("general.add") }}
      </v-btn>
    </template>

    <v-list>
      <v-list-item
        v-for="(action, index) in actions"
        :key="index"
        :data-testid="`allow-block-page-add-action-${action}`"
        @click="onActionClick(action)"
      >
        <v-list-item-title>
          {{ $t(`devicesSettings.allowBlockList.actions.${action}`) }}
        </v-list-item-title>
      </v-list-item>
    </v-list>
  </v-menu>
</template>

<script lang="ts">
import { computed, defineComponent } from "vue";
import { useDialogsStore } from "@/_store/dialogs.module";
import { ModalWidth } from "@/constants/modals";
import ImportCsvModal from "@/components/modals/ImportCSVModal.vue";
import { isMspActionRestricted } from "@/_helpers/msp-permissions.ts";
import { DeviceAllowListActions } from "@/constants/devices.ts";
import AllowBlockListDevicesModal from "@/components/modals/AllowBlocklistDevicesModal.vue";
import { DataSizeUnit } from "@/constants/general.ts";
import type { ComponentDialogConfig } from "@/types.ts";

import { useMspEndpointSecurityAllowBlockListStore } from "@/_store/msp/allow-block-lists/msp-endpoint-security-allow-block-list.module.ts";
import { useMspEdrAllowBlockListStore } from "@/_store/msp/allow-block-lists/msp-edr-allow-block-list.module.ts";
import { componentDialogsConfigConstructor } from "@/_helpers/dialog-helpers.ts";
import { MspPortalScopeSection, MspRolePermissionScopes } from "@/constants/roles.ts";

export default defineComponent({
  props: {
    isEdr: {
      type: Boolean,
      required: true,
    },
  },
  setup(props) {
    const dialogsStore = useDialogsStore();

    const allowBlockListStore = props.isEdr
      ? useMspEdrAllowBlockListStore()
      : useMspEndpointSecurityAllowBlockListStore();

    const actions = props.isEdr
      ? [
          DeviceAllowListActions.EDR_ADD_TO_ALLOWLIST,
          DeviceAllowListActions.EDR_ADD_TO_BLOCKLIST,
          DeviceAllowListActions.IMPORT_ALLOW_BLOCK_LIST_CSV,
        ]
      : [
          DeviceAllowListActions.ENDPOINT_ADD_TO_ALLOWLIST,
          DeviceAllowListActions.ENDPOINT_ADD_TO_BLOCKLIST,
          DeviceAllowListActions.IMPORT_ALLOW_BLOCK_LIST_CSV,
        ];

    const actionNotAllowed = computed(() =>
      isMspActionRestricted(
        MspRolePermissionScopes.MSP_PORTAL_SCOPE,
        MspPortalScopeSection.MSP_ALLOW_BLOCK_LISTS
      )
    );

    const getActionConfig = (
      actionKey: DeviceAllowListActions
    ): ComponentDialogConfig<
      | { isEdr: boolean }
      | {
          downloadTemplateCallback: () => Promise<void>;
          uploadCallback: (file: File) => Promise<unknown>;
          showDownloadReport: boolean;
          maxFileSize: number;
        }
    > => {
      const commonAddConfig = {
        action: actionKey,
        component: AllowBlockListDevicesModal,
        width: ModalWidth.LARGE,
        item: { isEdr: props.isEdr },
        callback: allowBlockListStore.addToAllowBlockList,
      };

      switch (actionKey) {
        case DeviceAllowListActions.IMPORT_ALLOW_BLOCK_LIST_CSV:
          return {
            action: actionKey,
            component: ImportCsvModal,
            width: ModalWidth.MEDIUM,
            item: {
              downloadTemplateCallback: allowBlockListStore.downloadAllowBlockListExampleCsv,
              uploadCallback: async (file: File) => {
                const response = await allowBlockListStore.importAllowBlockListFromCsv(file);
                await allowBlockListStore.getItems();
                return response;
              },
              showDownloadReport: true,
              maxFileSize: DataSizeUnit.MB * 10,
            },
            hideFooter: true,
            callback: () => {},
          };
        default:
          return commonAddConfig;
      }
    };

    const onActionClick = (actionKey: DeviceAllowListActions) => {
      const config = {
        ...getActionConfig(actionKey),
        action: actionKey,
        disable: actionNotAllowed.value,
      };

      const dialogConfig = componentDialogsConfigConstructor(config);
      dialogsStore.openDialog(dialogConfig);
    };

    return {
      actions,
      onActionClick,
    };
  },
});
</script>
